import PagedSearchTable, { PagedTableFunctions } from '../../components/PagedSearchTable'
import React, { useContext, useRef, useState } from 'react'
import Dialog from '../../components/Dialog'
import { EditRow, EditTable, focusFirstInput } from '../../components/Fields'
import AppContext from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimeProductResponse from '../../controllers/LimeProductResponse'
import LimeProductRequest from '../../controllers/LimeProductRequest'
import LimeProductType from '../../controllers/LimeProductType'
import { useValidation } from '../../validation'
import { buildSetter } from '../../immutableState'
import Input from '../../components/Input'
import Number from '../../components/Number'
import NumberNullable from '../../components/NumberNullable'
import SelectString from '../../components/SelectString'
import { showSuccessOrFailed } from '../../Snacks'

const emptyProduct: LimeProductRequest = {
    id: 0,
    name: '',
    type: LimeProductType.Other,
    caPercent: 0,
    caCO3Percent: 0,
    caOPercent: 0,
    caSO4Percent: 0,
    mgPercent: 0,
    mgCO3Percent: 0,
    mgOPercent: 0,
    ccePercent: 0,
    sPercent: 0,
    pPercent: 0,
    ph: null,
    active: true
}

function toForm(item: LimeProductResponse): LimeProductRequest {
    return {
        id: item.id,
        name: item.name,
        type: item.type,
        caPercent: item.caPercent,
        caCO3Percent: item.caCO3Percent,
        caOPercent: item.caOPercent,
        caSO4Percent: item.caSO4Percent,
        mgPercent: item.mgPercent,
        mgCO3Percent: item.mgCO3Percent,
        mgOPercent: item.mgOPercent,
        ccePercent: item.ccePercent,
        sPercent: item.sPercent,
        pPercent: item.pPercent,
        ph: item.ph,
        active: item.active
    }
}

const productTypes = Object.values(LimeProductType).map(t => ({ id: t, name: t }))

const LimingProducts: React.FC = () => {
    const context = useContext(AppContext)
    const pagedTableRef = useRef<PagedTableFunctions<LimeProductResponse>>()
    const [show, setShow] = useState(false)
    const [upsertData, setUpsertData] = useState<LimeProductRequest>(emptyProduct)
    const setData = buildSetter(upsertData, setUpsertData)

    function showPopup(preset: LimeProductRequest) {
        setUpsertData(preset)
        setShow(true)
    }

    function upsertCallback(data: LimeProductRequest) {
        if (!validation.validate()) { return }

        showSuccessOrFailed(
            context,
            LimeController.productUpsert(data),
            data.id === 0 ? context.word('product_added') : context.word('product_updated')
        ).then(() => {
            pagedTableRef.current?.refresh()
            setShow(false)
        })
    }

    function setActive(id: number, state: boolean) {
        showSuccessOrFailed(context, LimeController.productToggleActive({
            id,
            active: state
        })).then(() => {
            pagedTableRef.current?.refresh()
        })
    }

    const strikeOut = (product: LimeProductResponse): string => {
        return !product.active ? 'text-red-500 italic line-through' : ''
    }

    const validation = useValidation({
        nameRequired: () => upsertData.name.length > 0
    })

    return (
        <>
            <Dialog
                mounted={focusFirstInput}
                title={upsertData.id === 0 ? context.word('add_product') : context.word('edit_product')}
                show={show}
                setShow={setShow}
                body={
                    <EditTable
                        save={() => upsertCallback(upsertData)}
                        discard={() => setShow(false)}
                        saveWord={upsertData.id === 0 ? 'insert' : 'update'}
                    >
                        {EditRow(
                            context.word('name'),
                            <Input value={upsertData.name} change={v => setData({ name: v })} />,
                            validation.rules.nameRequired,
                            context.word('name_required')
                        )}
                        {EditRow(
                            context.word('type'),
                            <SelectString
                                options={productTypes}
                                textFunc={t => t.name}
                                valueFunc={t => t.id}
                                value={upsertData.type}
                                onChange={v => setData({ type: v as LimeProductType })}
                            />
                        )}
                        {EditRow(
                            '% Ca',
                            <Number value={upsertData.caPercent} change={v => setData({ caPercent: v })} />
                        )}
                        {EditRow(
                            '% CaCO3',
                            <Number value={upsertData.caCO3Percent} change={v => setData({ caCO3Percent: v })} />
                        )}
                        {EditRow(
                            '% CaO',
                            <Number value={upsertData.caOPercent} change={v => setData({ caOPercent: v })} />
                        )}
                        {EditRow(
                            '% CaSO4',
                            <Number value={upsertData.caSO4Percent} change={v => setData({ caSO4Percent: v })} />
                        )}
                        {EditRow(
                            '% Mg',
                            <Number value={upsertData.mgPercent} change={v => setData({ mgPercent: v })} />
                        )}
                        {EditRow(
                            '% MgCO3',
                            <Number value={upsertData.mgCO3Percent} change={v => setData({ mgCO3Percent: v })} />
                        )}
                        {EditRow(
                            '% MgO',
                            <Number value={upsertData.mgOPercent} change={v => setData({ mgOPercent: v })} />
                        )}
                        {EditRow(
                            '% CCE',
                            <Number value={upsertData.ccePercent} change={v => setData({ ccePercent: v })} />
                        )}
                        {EditRow(
                            '% S',
                            <Number value={upsertData.sPercent} change={v => setData({ sPercent: v })} />
                        )}
                        {EditRow(
                            '% P',
                            <Number value={upsertData.pPercent} change={v => setData({ pPercent: v })} />
                        )}
                        {EditRow(
                            'pH',
                            <NumberNullable value={upsertData.ph} change={v => setData({ ph: v })} />
                        )}
                    </EditTable>
                }
            />

            <div className="btn bg-primary m-2" onClick={() => showPopup(emptyProduct)}>
                {context.word('new_product')}
            </div>

            <PagedSearchTable<LimeProductResponse>
                componentRef={pagedTableRef}
                call={LimeController.productsPaged}
                columns={[
                    { header: context.word('id'), row: item => <div className={strikeOut(item)}>{item.id}</div> },
                    { header: context.word('name'), row: item => <div className={strikeOut(item)}>{item.name}</div> },
                    { header: context.word('type'), row: item => <div className={strikeOut(item)}>{item.type}</div> },
                    { header: '% Ca', row: item => <div className={strikeOut(item)}>{item.caPercent}</div> },
                    { header: '% CaCO3', row: item => <div className={strikeOut(item)}>{item.caCO3Percent}</div> },
                    { header: '% CaO', row: item => <div className={strikeOut(item)}>{item.caOPercent}</div> },
                    { header: '% CaSO4', row: item => <div className={strikeOut(item)}>{item.caSO4Percent}</div> },
                    { header: '% Mg', row: item => <div className={strikeOut(item)}>{item.mgPercent}</div> },
                    { header: '% MgCO3', row: item => <div className={strikeOut(item)}>{item.mgCO3Percent}</div> },
                    { header: '% MgO', row: item => <div className={strikeOut(item)}>{item.mgOPercent}</div> },
                    { header: '% CCE', row: item => <div className={strikeOut(item)}>{item.ccePercent}</div> },
                    { header: '% S', row: item => <div className={strikeOut(item)}>{item.sPercent}</div> },
                    { header: '% P', row: item => <div className={strikeOut(item)}>{item.pPercent}</div> },
                    { header: 'pH', row: item => <div className={strikeOut(item)}>{item.ph ?? '-'}</div> },
                    {
                        header: context.word('action'),
                        row: item => (
                            <div>
                                <span
                                    className="underline cursor-pointer"
                                    onClick={() => showPopup(toForm(item))}
                                >
                                    {context.word('edit')}
                                </span>
                                <span>&nbsp;|&nbsp;</span>
                                <span
                                    className="underline cursor-pointer"
                                    onClick={() => setActive(item.id, !item.active)}
                                >
                                    {item.active ? context.word('deactivate') : context.word('activate')}
                                </span>
                            </div>
                        )
                    }
                ]}
                keyExtractor={i => i.id}
            />
        </>
    )
}

export default LimingProducts
